import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import {
    ColorPalette,
    type TextProps,
    SkeletonTypography,
    Theme,
    Typography,
    TypographyCustom,
    Text,
    TextElement,
    useHeadingLevel,
} from '@lumapps/lumx/react';

import { useBlockPlaylistContext } from '../BlockPlaylistContext';
import { Button } from '../Button';
import { CLASSNAME as BLOCK_PLAYLIST_CLASSNAME } from '../constants';
import { Link } from '../Link';

import './Title.scss';
// eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
const { custom, ...typographyInterface } = Typography;

// Map used to be sure that Heading and SkeletonTypography typography types match
const SKELETON_TYPOGRAPHY_MAP = {
    ...typographyInterface,
    [TypographyCustom.body]: Typography.body1,
    [TypographyCustom.title1]: Typography.title,
    [TypographyCustom.title2]: Typography.title,
    [TypographyCustom.title3]: Typography.title,
    [TypographyCustom.title4]: Typography.title,
    [TypographyCustom.title5]: Typography.title,
    [TypographyCustom.title6]: Typography.title,
};

interface TitleProps extends Pick<TextProps, 'truncate' | 'typography'> {
    /**
     * Custom component to render the text.
     */
    as?: TextElement;
    /** Component children */
    children?: React.ReactNode;
    /** Typography variant */
    typography: keyof typeof SKELETON_TYPOGRAPHY_MAP;
}

export const CLASSNAME = `${BLOCK_PLAYLIST_CLASSNAME}-title` as const;

export const Title = ({ as, children, truncate, typography }: TitleProps) => {
    const { block } = useClassnames(CLASSNAME);

    const { canDisplay, isLoading, playlist, theme } = useBlockPlaylistContext();
    const { headingElement } = useHeadingLevel();

    if (!canDisplay('TITLE')) {
        return null;
    }

    if (isLoading || !playlist) {
        return (
            <SkeletonTypography
                className={block({ skeleton: true })}
                theme={theme}
                typography={SKELETON_TYPOGRAPHY_MAP[typography]}
                width="45%"
            />
        );
    }

    const hasInteractiveChild = React.Children.toArray(children).some(
        (child) => React.isValidElement(child) && (child.type === Link || child.type === Button),
    );

    const title = typeof playlist.title === 'string' ? playlist.title : playlist.title.value;

    return (
        <Text
            as={as || headingElement}
            className={block({ 'is-interactive': hasInteractiveChild })}
            color={theme === Theme.light ? ColorPalette.dark : ColorPalette.light}
            truncate={truncate}
            typography={typography}
        >
            {children || title}
        </Text>
    );
};
